"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function PrivacyPolicy() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const statement = "We are committed to protecting your |privacy| and ensuring that your personal data is handled with the highest level of security, transparency, and professional integrity.";
  const words = statement.split(" ");
  const stats = [{
    number: "100%",
    label: "Data Encryption"
  }, {
    number: "24/7",
    label: "Security Monitoring"
  }, {
    number: "0",
    label: "Third-Party Sharing"
  }];
  const ctaButtons = [{
    label: "Contact KTEX",
    to: "/#contact",
    newTab: false
  }, {
    label: "Become a Buyer",
    to: "/BecomeABuyerPage",
    newTab: false
  }, {
    label: "Become a Supplier",
    to: "/BecomeASupplierPage",
    newTab: false
  }, {
    label: "WhatsApp",
    to: "https://wa.me/971529096913",
    newTab: true
  }];
  return <>
      <section data-section-id="2249" id="privacy-policy" className="relative overflow-hidden bg-[var(--color-background)] py-32 md:py-44 lg:py-[180px] text-[var(--color-foreground)] isolate">
        {}
        <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[600px] w-[600px] -translate-x-1/2 -translate-y-[60%] rounded-lg opacity-[0.18] blur-3xl" style={{
        background: "radial-gradient(circle at center, var(--color-primary-500) 0%, transparent 70%)"
      }} />

        <div className="relative z-10 mx-auto max-w-6xl px-6">
          {}
          <div ref={eyebrowRef} className={`flex flex-col items-center transition-all duration-700 ease-out ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div aria-hidden="true" className="h-px w-[60px]" style={{
            backgroundColor: "var(--color-primary-500)"
          }} />
            <span className="mt-6 font-default text-[12px] uppercase" style={{
            letterSpacing: "0.25em",
            color: "var(--color-primary-500)"
          }}>
              Our Commitment
            </span>
          </div>

          {}
          <h2 ref={headlineRef} className="mx-auto mt-12 max-w-[880px] text-center font-default font-light leading-[1.2] text-[var(--color-foreground)]" style={{
          fontSize: "clamp(2rem, 5vw, 3.5rem)"
        }}>
            {words.map((word, i) => {
            const isItalic = word.startsWith("|") && word.endsWith("|");
            const cleanWord = isItalic ? word.slice(1, -1) : word;
            return <span key={i} data-index={i} className="inline-block transition-all ease-out" style={{
              opacity: headlineVisible ? 1 : 0,
              transform: headlineVisible ? "translateY(0)" : "translateY(20px)",
              transitionDuration: "800ms",
              transitionDelay: `${i * 60}ms`
            }}>
                  {isItalic ? <em className="font-default italic" style={{
                fontWeight: 400
              }}>
                      {cleanWord}
                    </em> : cleanWord}
                  {i < words.length - 1 && "\u00A0"}
                </span>;
          })}
          </h2>

          {}
          <div ref={statsRef} className={`mt-20 md:mt-24 lg:mt-[96px] transition-all duration-700 ease-out ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: "300ms"
        }}>
            <div className="flex flex-col items-stretch gap-10 md:flex-row md:items-center md:justify-center md:gap-0">
              {stats.map((stat, i) => <div key={i} data-index={i} className="flex flex-1 items-center">
                  <div className="flex flex-1 flex-col items-center text-center px-4 md:px-8">
                    <span className="font-default font-light leading-none text-[var(--color-foreground)]" style={{
                  fontSize: "clamp(2.5rem, 4.5vw, 3.5rem)"
                }}>
                      {stat.number}
                    </span>
                    <span className="mt-4 font-default text-[11px] uppercase text-[var(--color-primary-700)]" style={{
                  letterSpacing: "0.2em"
                }}>
                      {stat.label}
                    </span>
                  </div>
                  {i < stats.length - 1 && <Separator orientation="vertical" className="hidden md:block h-16 bg-[var(--color-primary-200)]" />}
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Full Bilingual Privacy Policy Legal Content */}
      <section className="bg-[var(--color-background)] py-16 md:py-24 text-[var(--color-foreground)] border-t border-[var(--color-border)]">
        <div className="mx-auto max-w-5xl px-6">
          <div className="space-y-12">
            
            {/* Section 1: About Us */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-4">
                1. مقدمة عنا — About Us
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-[var(--color-foreground)]">
                <div dir="rtl" className="text-right border-r-2 border-[var(--color-primary-500)] pr-4">
                  <p className="mb-2">
                    شركة كيتكس (KTEX FZCO) هي شركة تجارية رائدة مسجلة في المنطقة الحرة بميدان ومركز دبي للسلع المتعددة / دافزا (DAFZA)، دبي، دولة الإمارات العربية المتحدة. يرتكز نشاطنا الأساسي على التجارة الدولية المعتمدة للإلكترونيات بين الشركات (B2B).
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>اسم الشركة: KTEX FZCO</li>
                    <li>الموقع: Meydan Freezone / DAFZA، دبي، الإمارات العربية المتحدة</li>
                    <li>البريد الإلكتروني: info@ktex.ae</li>
                    <li>الموقع الإلكتروني: https://ktex.ae</li>
                    <li>النشاط التجاري: تجارة الإلكترونيات العالمية بين الشركات (B2B Electronics Trading)</li>
                  </ul>
                </div>
                <div className="border-l-2 border-[var(--color-primary-500)] pl-4">
                  <p className="mb-2">
                    KTEX FZCO is a global B2B electronics trading enterprise registered in Meydan Freezone and DAFZA, Dubai, United Arab Emirates. We are committed to transparency and the highest standards of data governance.
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Company Name: KTEX FZCO</li>
                    <li>Location: Meydan Freezone / DAFZA, Dubai, UAE</li>
                    <li>Email: <a href="mailto:info@ktex.ae" className="text-[var(--color-primary-500)] underline">info@ktex.ae</a></li>
                    <li>Website: <Link to="https://ktex.ae" newTab className="text-[var(--color-primary-500)] underline">https://ktex.ae</Link></li>
                    <li>Business Scope: Global B2B electronics trading & supply chain</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Section 2: Data Collection */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-4">
                2. البيانات التي نجمعها — Data Collection
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-[var(--color-foreground)]">
                <div dir="rtl" className="text-right border-r-2 border-[var(--color-primary-500)] pr-4">
                  <p className="mb-2">
                    نقوم بجمع ومعالجة المعلومات الضرورية لتقديم خدماتنا التجارية بأعلى كفاءة وأمان، وتتضمن:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>معلومات التسجيل والحساب المؤسسي (اسم الشركة، ممثل الشركة، السجل التجاري).</li>
                    <li>بيانات الشراء والطلبات وسجل التعاملات التجارية (Purchase & Order Data).</li>
                    <li>بيانات التصفح واستخدام المنصة الفنية (Browsing Data & IP Logs).</li>
                    <li>بيانات التواصل (البريد الإلكتروني، رقم الهاتف، عنوان الشحن والمراسلات).</li>
                    <li>بيانات الدفع والمعاملات المالية المصرفية الآمنة (دون تخزين أرقام البطاقات الائتمانية نهائياً).</li>
                  </ul>
                </div>
                <div className="border-l-2 border-[var(--color-primary-500)] pl-4">
                  <p className="mb-2">
                    We collect essential information required to facilitate trade workflows and ensure transaction security:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Corporate registration details (company name, authorized representative, trade license).</li>
                    <li>Purchase, RFQ, and order fulfillment records.</li>
                    <li>Technical browsing data, session diagnostics, and analytics logs.</li>
                    <li>Direct contact details (corporate email, phone number, operational address).</li>
                    <li>Payment confirmation data processed through certified gateways without storing credit card numbers.</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Section 3: Data Usage */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-4">
                3. كيفية استخدام البيانات — Data Usage
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-[var(--color-foreground)]">
                <div dir="rtl" className="text-right border-r-2 border-[var(--color-primary-500)] pr-4">
                  <p className="mb-2">تُستخدم البيانات التي يتم جمعها للأغراض التشغيلية والقانونية التالية فقط:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>تنفيذ وتجهيز وتوصيل طلبيات التجارة الإلكترونية وسلاسل التوريد.</li>
                    <li>إرسال تأكيدات الطلبات، الفواتير الرسمية، وإشعارات الشحن.</li>
                    <li>تقديم الدعم الفني وخدمة العملاء على مدار الساعة.</li>
                    <li>تحسين جودة المنصة وخدمات التداول بناءً على التحليلات التشغيلية.</li>
                    <li>إرسال العروض والتحديثات الحصرية بناءً على موافقة صريحة مسبقة من المستخدم.</li>
                    <li>الامتثال للأنظمة والتشريعات التجارية والضريبية في دولة الإمارات والقوانين الدولية.</li>
                  </ul>
                </div>
                <div className="border-l-2 border-[var(--color-primary-500)] pl-4">
                  <p className="mb-2">Collected personal and corporate data is utilized strictly for lawful purposes:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Executing B2B purchase orders, shipping logistics, and customs handling.</li>
                    <li>Transmitting order confirmations, commercial invoices, and consignment updates.</li>
                    <li>Delivering dedicated account management and client support.</li>
                    <li>Enhancing digital platform performance and user experience.</li>
                    <li>Sharing targeted industry updates and inventory offerings with prior opt-in consent.</li>
                    <li>Complying with UAE regulations, anti-fraud standards, and international trade laws.</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Section 4: GDPR Compliance */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-4">
                4. GDPR والامتثال — GDPR Compliance
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-[var(--color-foreground)]">
                <div dir="rtl" className="text-right border-r-2 border-[var(--color-primary-500)] pr-4">
                  <p className="mb-2">
                    نلتزم باللائحة العامة لحماية البيانات (GDPR) لجميع شركائنا وعملائنا داخل الاتحاد الأوروبي وحول العالم، ونكفل الحقوق الكاملة التالية:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>الحق في الوصول إلى البيانات الشخصية المخزنة وطلب نسخة منها.</li>
                    <li>الحق في تصحيح وتعديل أي بيانات غير دقيقة أو غير مكتملة.</li>
                    <li>الحق في محو البيانات (&quot;الحق في النسيان&quot;) وفقاً للضوابط القانونية.</li>
                    <li>الحق في نقل البيانات إلى مزود خدمة آخر بصيغة رقمية منظمة.</li>
                    <li>الحق في الاعتراض على معالجة البيانات أو تقييد نطاق استخدامها.</li>
                  </ul>
                </div>
                <div className="border-l-2 border-[var(--color-primary-500)] pl-4">
                  <p className="mb-2">
                    In compliance with the General Data Protection Regulation (GDPR) for global business partners, we guarantee the following fundamental rights:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Right of Access: Request copies of any stored personal and account data.</li>
                    <li>Right to Rectification: Correct inaccurate or outdated information.</li>
                    <li>Right to Erasure: Request permanent deletion of data where legally permissible.</li>
                    <li>Right to Data Portability: Receive data in a structured, machine-readable format.</li>
                    <li>Right to Object: Opt out or restrict specific data processing operations.</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Section 5: Email Marketing */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-4">
                5. التسويق عبر البريد الإلكتروني — Email Marketing
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-[var(--color-foreground)]">
                <div dir="rtl" className="text-right border-r-2 border-[var(--color-primary-500)] pr-4">
                  <p className="mb-2">نتبع سياسة صارمة وخالية من الرسائل المزعجة (No Spam Policy):</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>نستخدم منصة Mailchimp المعتمدة لإدارة القوائم البريدية والتواصل الاحترافي.</li>
                    <li>الاشتراك في النشرة الإخبارية اختياري بنسبة 100% ويخضع لموافقة صريحة.</li>
                    <li>يمكن إلغاء الاشتراك في أي وقت بنقرة واحدة عبر الرابط الموجود بأسفل كل رسالة.</li>
                    <li>تقتصر الرسائل التسويقية على نشرات دورية شهرية وعروض الجملة الهامة فقط.</li>
                    <li>لا نقوم بمشاركة أو بيع أو تأجير قوائمنا البريدية لأي طرف ثالث تحت أي ظرف.</li>
                  </ul>
                </div>
                <div className="border-l-2 border-[var(--color-primary-500)] pl-4">
                  <p className="mb-2">We maintain a strict anti-spam communication policy:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>We utilize enterprise Mailchimp systems for reliable and secure mailing list management.</li>
                    <li>Subscription is 100% voluntary and opt-in only.</li>
                    <li>Unsubscribe at any time instantly via the direct link included in every communication.</li>
                    <li>Outbound campaigns are limited to monthly industry digests and critical market updates.</li>
                    <li>We never sell, rent, or trade email distribution lists with external third parties.</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Section 6: Third-Party Services */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-4">
                6. الخدمات الخارجية — Third-Party Services
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-[var(--color-foreground)]">
                <div dir="rtl" className="text-right border-r-2 border-[var(--color-primary-500)] pr-4">
                  <p className="mb-2">
                    نتعامل حصرياً مع مزودي خدمات عالميين معتمدين يلتزمون بأعلى معايير الأمان وحماية الخصوصية:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Mailchimp: لإدارة وتوزيع النشرات الإخبارية والتحديثات التشغيلية.</li>
                    <li>بوابات الدفع الإلكتروني المعتمدة: لمعالجة المعاملات المالية المشفرة والمتوافقة مع معايير PCI-DSS.</li>
                    <li>Google Analytics: لتحليل أداء المنصة الرقمية وتحسين تجربة التصفح دون جمع معرفات شخصية مباشرة.</li>
                    <li>خوادم واستضافة سحابية آمنة ومعتمدة بأحدث بروتوكولات الحماية السحابية.</li>
                  </ul>
                </div>
                <div className="border-l-2 border-[var(--color-primary-500)] pl-4">
                  <p className="mb-2">
                    We partner exclusively with verified international infrastructure and service providers:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Mailchimp: Cloud newsletter distribution and transactional notifications.</li>
                    <li>Certified Payment Gateways: Encrypted, PCI-DSS compliant checkout and escrow processing.</li>
                    <li>Google Analytics: Aggregated traffic measurement and user journey optimization.</li>
                    <li>Enterprise Cloud Hosting: Certified high-availability servers with automated threat protection.</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Section 7: Data Security */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-4">
                7. أمان البيانات — Data Security
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-[var(--color-foreground)]">
                <div dir="rtl" className="text-right border-r-2 border-[var(--color-primary-500)] pr-4">
                  <p className="mb-2">نطبق بروتوكولات أمنية دفاعية متعددة الطبقات لحماية بياناتكم المؤسسية:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>تشفير شامل للموقع والاتصالات باستخدام بروتوكول SSL/HTTPS عالي الأمان (256-bit).</li>
                    <li>تشفير قوي لكلمات المرور والبيانات الحساسة في قواعد البيانات المشفرة.</li>
                    <li>صلاحيات وصول مقيدة ومحصورة بالموظفين المخولين والمكلفين بالعمليات المباشرة فقط.</li>
                    <li>نسخ احتياطي يومي مشفر لضمان سلامة واستمرارية الأعمال.</li>
                    <li>عدم تخزين أرقام بطاقات الائتمان أو رموز الأمان (CVV) على خوادمنا نهائياً.</li>
                  </ul>
                </div>
                <div className="border-l-2 border-[var(--color-primary-500)] pl-4">
                  <p className="mb-2">We enforce multi-tiered defense and encryption protocols across our infrastructure:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Sitewide 256-bit SSL/HTTPS encryption for all data in transit.</li>
                    <li>Strong cryptographic hashing for credentials and sensitive stored entities.</li>
                    <li>Role-based access control restricting operational data strictly to vetted personnel.</li>
                    <li>Daily automated encrypted backups with isolated recovery endpoints.</li>
                    <li>Zero credit card data storage policy—all sensitive billing is tokenized directly by gateways.</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Section 8: Your Rights */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-4">
                8. حقوقك — Your Rights
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-[var(--color-foreground)]">
                <div dir="rtl" className="text-right border-r-2 border-[var(--color-primary-500)] pr-4">
                  <p className="mb-2">بصفتك مستخدماً أو شريكاً تجارياً، يحق لك في أي وقت ممارسة حقوقك القانونية التالية:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>طلب نسخة كاملة ومفصلة من بياناتك الشخصية المخزنة لدينا.</li>
                    <li>طلب تصحيح أو تحديث أي معلومات خاطئة أو غير دقيقة فوراً.</li>
                    <li>طلب حذف سجلاتك وبياناتك الشخصية من أنظمتنا نهائياً.</li>
                    <li>طلب إيقاف أو تجميد معالجة بياناتك لأي غرض تسويقي أو تحليلي.</li>
                  </ul>
                </div>
                <div className="border-l-2 border-[var(--color-primary-500)] pl-4">
                  <p className="mb-2">As an account holder or trading partner, you retain full sovereignty over your data:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Request a comprehensive disclosure report of your personal data on file.</li>
                    <li>Request immediate rectification of erroneous or outdated corporate records.</li>
                    <li>Request complete data deletion and account termination.</li>
                    <li>Instruct us to halt or restrict specific data processing workflows.</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Section 9: UAE Compliance */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-4">
                9. الامتثال للقوانين المحلية — UAE & International Compliance
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-[var(--color-foreground)]">
                <div dir="rtl" className="text-right border-r-2 border-[var(--color-primary-500)] pr-4">
                  <p className="mb-2">تخضع جميع عملياتنا لمعايير الامتثال القانوني الصارمة وفق:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>قانون المعاملات والتجارة الإلكترونية الإماراتي رقم 1 لسنة 2006 وتعديلاته.</li>
                    <li>قوانين حماية المستهلك وحماية البيانات الشخصية في دولة الإمارات العربية المتحدة.</li>
                    <li>لوائح ومعايير المنطقة الحرة بميدان وسلطة دبي للمناطق الحرة (DAFZA).</li>
                    <li>قوانين حماية الخصوصية والبيانات الدولية بما في ذلك GDPR و CCPA.</li>
                  </ul>
                </div>
                <div className="border-l-2 border-[var(--color-primary-500)] pl-4">
                  <p className="mb-2">All company operations and data management practices comply fully with:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>UAE Federal Law No. (1) of 2006 Concerning Electronic Transactions and Commerce.</li>
                    <li>UAE Federal Data Protection and Consumer Protection regulatory frameworks.</li>
                    <li>Meydan Freezone & Dubai Airport Freezone (DAFZA) governance standards.</li>
                    <li>Applicable international standards including GDPR (EU) and CCPA provisions.</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Section 10: Contact Us */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-4">
                10. التواصل معنا — Contact Us
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-[var(--color-foreground)]">
                <div dir="rtl" className="text-right border-r-2 border-[var(--color-primary-500)] pr-4">
                  <p className="mb-2">
                    إذا كانت لديك أي استفسارات أو طلبات تتعلق بسياسة الخصوصية أو بحماية بياناتك، يرجى التواصل مع فريق الامتثال لدينا:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>البريد الإلكتروني: info@ktex.ae</li>
                    <li>الهاتف / واتساب: +971 52 909 6913</li>
                    <li>العنوان: Meydan Freezone / DAFZA، دبي، الإمارات العربية المتحدة</li>
                  </ul>
                </div>
                <div className="border-l-2 border-[var(--color-primary-500)] pl-4">
                  <p className="mb-2">
                    For any questions, verification requests, or data privacy inquiries, please contact our compliance desk:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Email: <a href="mailto:info@ktex.ae" className="text-[var(--color-primary-500)] underline">info@ktex.ae</a></li>
                    <li>Phone: <a href="tel:+971529096913" className="text-[var(--color-primary-500)] underline">+971 52 909 6913</a></li>
                    <li>Address: Meydan Freezone / DAFZA, Dubai, UAE</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Section 11: Last Updated */}
            <article className="rounded-lg border border-[var(--color-border)] bg-white p-6 md:p-8 shadow-sm text-center">
              <h2 className="text-xl md:text-2xl font-bold text-[var(--color-primary-500)] mb-2">
                11. آخر تحديث — Last Updated
              </h2>
              <p className="text-sm md:text-base text-[var(--color-foreground)] font-medium mb-0">
                7 أكتوبر 2026 | October 7, 2026
              </p>
            </article>

          </div>
        </div>
      </section>

      <section className="bg-[var(--color-secondary)] py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-col items-center justify-center gap-4 md:flex-row md:gap-6">
            {ctaButtons.map((btn, i) => <Button key={i} data-index={i} asChild className="btn-secondary min-w-[200px] shadow-md">
                <Link to={btn.to} newTab={btn.newTab}>
                  {btn.label}
                </Link>
              </Button>)}
          </div>
        </div>
      </section>
    </>;
}